import moment from "moment";
import { FormEvent, ReactNode, useState } from "react";
import { useDropzone } from "react-dropzone";
import {
  CONVERSATION_ATTACHMENT_LIMIT_BYTES,
  CONVERSATION_ATTACHMENT_SIZE_ERROR_MESSAGE,
  getConversationAttachmentDropzoneErrorMessage,
} from "../../../../Helper/ConversationAttachmentUpload";
import Communicator from "../../../../Helper/Communicator";
import { useChatWidgetText } from "../../../../Helper/ChatWidget";
import { useConfigStore } from "../../../../Stores/Config";
import { validateEmail } from "../../../../Stores/Form";
import { useTicketStore } from "../../../../Stores/Ticket";
import { ratingOptions } from "../../../RatingFormItem/RatingFormItem";
import { Button, Card, cx, Foot } from "../../ChatWidgetPrimitives";
import "../FormCard.scss";
import "../QuickRepliesCard.scss";
import "./StepCards.scss";

/**
 * A workflow step's input as a chat card (text, email, number, long text,
 * dropdown, multiple choice, upload, buttons, rating). Same payloads as the
 * input components they replace; only the surface is the chat-card one.
 */
export const StepCard = ({ children }: { children: ReactNode }) => (
  <div className="bot-step">
    <div className="cw">{children}</div>
  </div>
);

const StepHead = ({ title, subtitle }: { title?: string; subtitle?: string }) =>
  title || subtitle ? (
    <div className="cw-form-head">
      {title && <div className="cw-h">{title}</div>}
      {subtitle && <div className="cw-sm cw-muted">{subtitle}</div>}
    </div>
  ) : null;

type TextKind = "text" | "email" | "number" | "textarea";

/** Attribute-backed (TEXT/EMAIL/NUMBER/TEXTAREA) and legacy text/email/number steps. */
export const StepInputCard = ({ action }: { action: any }) => {
  const ticketStore = useTicketStore();
  const text = useChatWidgetText();
  const [value, setValue] = useState("");
  const attribute = action?.attribute;
  const isLegacy = !attribute?.type;

  const kind: TextKind = isLegacy
    ? action.inputType === "email" || action.inputType === "number"
      ? action.inputType
      : "text"
    : attribute.type === "EMAIL"
      ? "email"
      : attribute.type === "NUMBER"
        ? "number"
        : attribute.type === "TEXTAREA"
          ? "textarea"
          : "text";
  const required = isLegacy ? !!action.required : !!attribute.required;
  const minValue = isLegacy ? action.minValue : attribute.minValue;
  const maxValue = isLegacy ? action.maxValue : attribute.maxValue;
  const placeholder = (isLegacy ? action.placeholder : attribute.placeholder) ?? "";

  const isValid = () => {
    const trimmed = value.trim();
    if (!trimmed) {
      // An empty answer only goes through when nothing is asked of it.
      return !required && kind !== "email" && kind !== "number";
    }
    if (!isLegacy && attribute.validation) {
      try {
        if (!new RegExp(attribute.validation).test(value)) return false;
      } catch (_) {
        // A broken pattern in the step config must not block the customer.
      }
    }
    if (kind === "number") {
      const number = Number(trimmed);
      if (!Number.isFinite(number)) return false;
      if (typeof minValue === "number" && number < minValue) return false;
      if (typeof maxValue === "number" && number > maxValue) return false;
    }
    if (kind === "email") return validateEmail(trimmed);
    return true;
  };
  const canSend = isValid();

  const send = (event?: FormEvent) => {
    event?.preventDefault();
    if (!canSend) return;
    if (isLegacy && action.aiCollect) {
      ticketStore.answerBotAction(
        {
          question: value,
          message: value,
          clientTools: useConfigStore.getState().agentTools,
          isFirstMessage: !ticketStore.hasAnswerBotReply(),
        },
        [],
      );
      return;
    }
    ticketStore.answerBotAction({ text: value, message: value }, []);
  };

  return (
    <StepCard>
      <Card className="cw-form" label={action.title}>
        <form onSubmit={send} noValidate>
          <StepHead title={action.title} />
          <div className="cw-form-group">
            <div className="cw-form-field bot-step-field">
              {kind === "textarea" ? (
                <textarea
                  className="cw-form-control"
                  aria-label={action.title}
                  aria-required={required}
                  placeholder={placeholder}
                  value={value}
                  rows={3}
                  onChange={(event) => setValue(event.target.value)}
                />
              ) : (
                <input
                  className="cw-form-control"
                  type={kind}
                  inputMode={kind === "number" ? "decimal" : undefined}
                  aria-label={action.title}
                  aria-required={required}
                  autoComplete={kind === "email" ? "email" : undefined}
                  placeholder={placeholder}
                  value={value}
                  min={kind === "number" ? minValue : undefined}
                  max={kind === "number" ? maxValue : undefined}
                  spellCheck={false}
                  onChange={(event) => setValue(event.target.value)}
                />
              )}
            </div>
          </div>
          <Foot>
            <Button type="submit" disabled={!canSend}>
              {text("widgetSubmit")}
            </Button>
          </Foot>
        </form>
      </Card>
    </StepCard>
  );
};

/** A choice list: up to six options as pills (one tap sends), more as a select. */
export const StepSelectCard = ({ action }: { action: any }) => {
  const ticketStore = useTicketStore();
  const text = useChatWidgetText();
  const [selected, setSelected] = useState("");
  const options: { value: string; label: string }[] =
    action?.attribute?.options ?? action?.options ?? [];

  const send = (option?: { value: string; label: string }) => {
    if (!option) return;
    ticketStore.answerBotAction({ text: option.value, message: option.label }, []);
  };

  if (options.length <= 6) {
    return (
      <StepCard>
        {action.title && <div className="bot-step-question">{action.title}</div>}
        <div className="cw-qr" role="group" aria-label={action.title}>
          {options.map((option) => (
            <button
              type="button"
              key={option.value}
              className="cw-qr-pill"
              onClick={() => send(option)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </StepCard>
    );
  }

  return (
    <StepCard>
      <Card className="cw-form" label={action.title}>
        <StepHead title={action.title} />
        <div className="cw-form-group">
          <div className="cw-form-field bot-step-field">
            <select
              className="cw-form-control"
              aria-label={action.title}
              value={selected}
              onChange={(event) => setSelected(event.target.value)}
            >
              <option value="" disabled>
                {text("widgetChooseOption")}
              </option>
              {options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <Foot>
          <Button
            disabled={!selected}
            onClick={() => send(options.find((option) => option.value === selected))}
          >
            {text("widgetSubmit")}
          </Button>
        </Foot>
      </Card>
    </StepCard>
  );
};

/** MULTISELECT: chips to toggle, then Send. */
export const StepMultiCard = ({ action }: { action: any }) => {
  const ticketStore = useTicketStore();
  const text = useChatWidgetText();
  const [picked, setPicked] = useState<string[]>([]);
  const options: { value: string; label: string }[] = action?.attribute?.options ?? [];
  const required = !!action?.attribute?.required;
  const canSend = picked.length > 0 || !required;

  const send = () => {
    if (!canSend) return;
    const labels = options
      .filter((option) => picked.includes(option.value))
      .map((option) => option.label);
    ticketStore.answerBotAction({ text: picked, message: labels.join(", ") }, []);
  };

  return (
    <StepCard>
      <Card className="cw-form" label={action.title}>
        <StepHead title={action.title} />
        <div className="cw-form-group">
          <div className="cw-form-field bot-step-field">
            <div className="cw-form-chips" role="group" aria-label={action.title}>
              {options.map((option) => {
                const isOn = picked.includes(option.value);
                return (
                  <button
                    type="button"
                    key={option.value}
                    aria-pressed={isOn}
                    className={cx("cw-form-chip", isOn && "cw-form-chip--on")}
                    onClick={() =>
                      setPicked((current) =>
                        isOn
                          ? current.filter((value) => value !== option.value)
                          : [...current, option.value],
                      )
                    }
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <Foot>
          <Button disabled={!canSend} onClick={send}>
            {text("widgetSubmit")}
          </Button>
        </Foot>
      </Card>
    </StepCard>
  );
};

/** The "Buttons" step: Kai's quick-reply pills. */
export const StepButtonsCard = ({ action }: { action: any }) => {
  const ticketStore = useTicketStore();
  const buttons: { text: string }[] = action?.buttons ?? [];
  return (
    <StepCard>
      <div className="cw-qr" role="group" aria-label={action.title}>
        {buttons.map((button, index) => (
          <button
            type="button"
            key={index}
            className="cw-qr-pill"
            onClick={() =>
              ticketStore.answerBotAction({ selected: index, message: button.text }, [])
            }
          >
            {button.text}
          </button>
        ))}
      </div>
    </StepCard>
  );
};

const RATING_VALUES = [2, 4, 6, 8, 10];

/** "Rate conversation": five faces; one tap sends. */
export const StepRatingCard = ({ action }: { action: any }) => {
  const ticketStore = useTicketStore();
  const text = useChatWidgetText();
  const expirationDays = action?.expirationDays;
  const expired =
    !!expirationDays &&
    expirationDays > 0 &&
    moment().diff(moment(action?.createdAt), "days", true) >= expirationDays;

  const rate = (index: number) => {
    if (expired) return;
    const value = RATING_VALUES[index];
    Communicator.notifyEvent("rating-sent", {
      type: "conversation-rating",
      rating: value,
      message: ratingOptions[index],
    });
    ticketStore.answerBotAction({ rating: value, message: ratingOptions[index] }, []);
  };

  return (
    <StepCard>
      <Card className="cw-form" label={action.title}>
        <StepHead title={action.title} />
        <div className="bot-step-rating" role="radiogroup" aria-label={action.title}>
          {ratingOptions.map((face: string, index: number) => (
            <button
              type="button"
              key={face}
              role="radio"
              aria-checked={false}
              disabled={expired}
              className="bot-step-face"
              onClick={() => rate(index)}
            >
              {face}
            </button>
          ))}
        </div>
        <div className="bot-step-rating-legend cw-sm cw-muted">
          <span>{text("widgetRatingLow")}</span>
          <span>{text("widgetRatingHigh")}</span>
        </div>
      </Card>
    </StepCard>
  );
};

/** UPLOAD: a drop area, the picked file, then Send. */
export const StepUploadCard = ({ action }: { action: any }) => {
  const ticketStore = useTicketStore();
  const text = useChatWidgetText();
  const [file, setFile] = useState<{ url: string; name: string } | undefined>();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const accept: Record<string, string[]> = {};
  const restrictions: string | undefined = action?.attribute?.restrictions;
  if (restrictions) {
    for (const type of restrictions.replace(/\s/g, "").split(",")) {
      if (type) accept[type] = [];
    }
  }

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    maxFiles: 1,
    multiple: false,
    noClick: true,
    accept,
    maxSize: CONVERSATION_ATTACHMENT_LIMIT_BYTES,
    onDrop: (accepted) => {
      const picked = accepted?.[0];
      if (!picked) return;
      setError(undefined);
      setUploading(true);
      ticketStore
        .uploadFile(picked)
        .then((url) => setFile({ url, name: picked.name }))
        .catch((err) => setError(err?.message || CONVERSATION_ATTACHMENT_SIZE_ERROR_MESSAGE))
        .finally(() => setUploading(false));
    },
    onDropRejected: (rejections: any[]) =>
      setError(getConversationAttachmentDropzoneErrorMessage(rejections)),
  });

  const send = () => {
    if (!file) return;
    ticketStore.answerBotAction({ text: file.url, message: file.name }, []);
  };

  return (
    <StepCard>
      <Card className="cw-form" label={action.title}>
        <StepHead title={action.title} />
        <div
          {...getRootProps({
            className: cx("bot-step-drop", isDragActive && "bot-step-drop--active"),
          })}
        >
          <input {...getInputProps({ "aria-label": action.title })} />
          {uploading ? (
            <span className="cw-spinner" aria-label={text("widgetUploading")} />
          ) : file ? (
            <span className="bot-step-file">{file.name}</span>
          ) : (
            <>
              <span className="bot-step-drop-title">{text("widgetDropFile")}</span>
              <span className="cw-sm cw-muted">{text("widgetDropFileHint")}</span>
            </>
          )}
        </div>
        {error && <div className="bot-step-error cw-sm">{error}</div>}
        <Foot>
          <Button variant="ghost" onClick={open} disabled={uploading}>
            {file ? text("widgetChooseAnother") : text("widgetChooseFile")}
          </Button>
          <Button disabled={!file || uploading} onClick={send}>
            {text("widgetSubmit")}
          </Button>
        </Foot>
      </Card>
    </StepCard>
  );
};
